import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmService, displayString, PointStatus, statusBadge, StatusBadge } from '@luzula/luzula-lib';

import { DataService } from 'src/app/data.service';
import { FilterService } from 'src/app/filter.service';
import { ProjectService } from 'src/app/project.service';
import { AuthService } from 'src/app/auth.service';
import {
	ControlDrill, ControlEvaluation, ControlEvalPoint, pointIdsForDrill, StatusSlice,
} from './control-evaluation';
import { personenangabenVerbergen } from '../../personenangaben';

/**
 * Kontroll-Auswertung pro Projekt: Punktstatus + automatische Validierungen +
 * Kommentare + Personen-Statistik.
 *
 * Interaktiv NUR fürs aktuell geöffnete Projekt (`interactive`): ein Klick auf
 * eine Kennzahl/Zeile filtert die Punktliste (= füllt die Auswahl) per
 * Drill-down und schließt das Modal. Für ein nicht geöffnetes Projekt (Aufruf
 * aus „Projekt wählen") sind die Kennzahlen read-only.
 */
@Component({
	standalone: true,
	selector: 'bp-control-evaluation-modal',
	imports: [CommonModule],
	templateUrl: './control-evaluation-modal.component.html',
	styleUrls: ['./control-evaluation-modal.component.css'],
})
export class ControlEvaluationModalComponent implements OnInit {
	@Input() projectId = '';
	@Input() projectName = '';

	activeModal = inject(NgbActiveModal);
	private ds = inject(DataService);
	private fs = inject(FilterService);
	private ps = inject(ProjectService);
	private modal = inject(NgbModal);
	private confirm = inject(ConfirmService);
	private aus = inject(AuthService);

	/**
	 * Wer Kartierer und Aufnahmedatum nicht sehen darf (Datennutzer, nicht
	 * Angemeldete), bekommt den Abschnitt „Bearbeiter & Kontrolleure" nicht —
	 * er listet Punkte je Person (Nutzer-Befund 06.09.2026, Gastmodus).
	 */
	get personenVerborgen(): boolean {
		return personenangabenVerbergen(this.aus);
	}

	loading = true;
	error = false;
	ev?: ControlEvaluation;
	/** Rohpunkte für den Drill-down (IDs je Kriterium). */
	private points: ControlEvalPoint[] = [];

	displayString = displayString;

	async ngOnInit(): Promise<void> {
		try {
			const res = await this.ds.getControlEvaluationForProject(this.projectId);
			this.ev = res.evaluation;
			this.points = res.points;
		} catch (e) {
			console.error('Kontroll-Auswertung konnte nicht geladen werden', e);
			this.error = true;
		} finally {
			this.loading = false;
		}
	}

	/** Drill-down nur fürs aktuell geöffnete Projekt — sonst sind die Punkte nicht in der Liste. */
	get interactive(): boolean {
		return !!this.projectId && this.projectId === this.ps.project();
	}

	/**
	 * Klick auf eine Kennzahl/Zeile: fragt zuerst nach (das Schließen + Wechsel zur
	 * Liste/Karte überrascht sonst), filtert dann die Punktliste auf die passenden
	 * Punkte (= füllt die Auswahl, benannter Ad-hoc-Filter `label`) und schließt
	 * alle Modale. No-op, wenn nicht interaktiv oder das Kriterium keine Treffer hat.
	 */
	async drill(drill: ControlDrill, label: string): Promise<void> {
		if (!this.interactive) return;
		const ids = pointIdsForDrill(this.points, drill);
		if (!ids.length) return;
		const n = ids.length;
		const ok = await this.confirm.confirm({
			title: 'Punktliste filtern',
			message: `Die Auswertung wird geschlossen und die Liste/Karte auf ${n} `
				+ `${n === 1 ? 'Punkt' : 'Punkte'} gefiltert („${label}“). `
				+ 'Diese Punkte werden zugleich ausgewählt.',
			confirmText: 'Filtern & anzeigen',
			cancelText: 'Abbrechen',
		});
		if (ok !== true) return;
		this.fs.applyAdHocView.emit({ label, ids });
		this.modal.dismissAll();
	}

	badge(status: PointStatus | null | undefined): StatusBadge | null {
		return statusBadge(status);
	}

	/** Anteil eines Status am Gesamtbalken in Prozent. */
	pct(count: number): number {
		const total = this.ev?.totalPoints ?? 0;
		return total ? (count / total) * 100 : 0;
	}

	/** Nur Status-Segmente mit count > 0 für den gestapelten Balken. */
	get visibleSlices(): StatusSlice[] {
		return this.ev?.statusSlices.filter(s => s.count > 0) ?? [];
	}
}
